import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

export interface Plan {
  id: string;
  name: string;
  slug: string;
  price: number;
  features: string[];
  stripe_product_id?: string;
  stripe_price_id?: string;
  display_order: number;
  active: boolean;
}

export interface ModuleMetier {
  id: string;
  name: string;
  slug: string;
  description: string;
  features: string[];
  active_by_default: boolean;
}

export interface ClientModule {
  id: string;
  user_id: string;
  module_id: string;
  active: boolean;
  activated_at: string;
  module?: ModuleMetier;
}

export interface PackAccess {
  currentPlan: Plan | null;
  isDafClient: boolean;
  activeModules: ClientModule[];
  hasAccess: (feature: string) => boolean;
  hasModule: (moduleSlug: string) => boolean;
  canAccessReports: boolean;
  canAccessDafSpace: boolean;
  loading: boolean;
}

export function usePackAccess(): PackAccess {
  const [currentPlan, setCurrentPlan] = useState<Plan | null>(null);
  const [isDafClient, setIsDafClient] = useState(false);
  const [activeModules, setActiveModules] = useState<ClientModule[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const timeout = setTimeout(() => {
      console.warn('[usePackAccess] Loading timeout exceeded, forcing completion');
      setLoading(false);
    }, 3000);

    loadPackAccess().finally(() => clearTimeout(timeout));

    return () => clearTimeout(timeout);
  }, []);

  const loadPackAccess = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setLoading(false);
        return;
      }

      const { data: subscription } = await supabase
        .from('subscriptions')
        .select('plan_type, status')
        .eq('user_id', user.id)
        .eq('status', 'active')
        .maybeSingle();

      if (subscription) {
        const planType = subscription.plan_type;

        if (planType === 'manual') {
          const { data: profile } = await supabase
            .from('user_profiles')
            .select('current_plan, is_daf_client')
            .eq('user_id', user.id)
            .maybeSingle();

          if (profile?.current_plan) {
            const { data: plan } = await supabase
              .from('plans')
              .select('*')
              .eq('slug', profile.current_plan)
              .maybeSingle();

            if (plan) {
              setCurrentPlan(plan);
              setIsDafClient(profile.is_daf_client || profile.current_plan === 'daf');
            }
          }
        } else {
          const { data: plan } = await supabase
            .from('plans')
            .select('*')
            .eq('slug', planType)
            .maybeSingle();

          if (plan) {
            setCurrentPlan(plan);
            setIsDafClient(planType === 'daf');
          }
        }
      } else {
        const { data: profile } = await supabase
          .from('user_profiles')
          .select('plan_id, is_daf_client')
          .eq('user_id', user.id)
          .maybeSingle();

        if (profile) {
          setIsDafClient(profile.is_daf_client || false);

          if (profile.plan_id) {
            const { data: plan } = await supabase
              .from('plans')
              .select('*')
              .eq('id', profile.plan_id)
              .maybeSingle();

            if (plan) {
              setCurrentPlan(plan);
            }
          }
        }
      }

      const { data: modules } = await supabase
        .from('client_modules')
        .select(`
          *,
          module:modules_metiers(*)
        `)
        .eq('user_id', user.id)
        .eq('active', true);

      if (modules) {
        setActiveModules(modules);
      }
    } catch (error) {
      console.error('Error loading pack access:', error);
    } finally {
      setLoading(false);
    }
  };

  const hasAccess = (feature: string): boolean => {
    if (!currentPlan) return false;

    const planSlug = currentPlan.slug;

    const baseFeatures = [
      'dashboard',
      'treasury',
      'transactions',
      'categorization',
      'cash_flow',
      'scenarios',
      'stress_test',
      'frd',
      'budget',
      'tracking',
      'multi_accounts'
    ];

    if (baseFeatures.includes(feature)) {
      return ['essentiel', 'metier', 'pilotage', 'daf'].includes(planSlug);
    }

    if (feature === 'modules_metiers') {
      return ['metier', 'pilotage', 'daf'].includes(planSlug);
    }

    if (feature === 'reports') {
      return ['pilotage', 'daf'].includes(planSlug);
    }

    if (feature === 'daf_space') {
      return planSlug === 'daf' && isDafClient;
    }

    return false;
  };

  const hasModule = (moduleSlug: string): boolean => {
    return activeModules.some(cm => cm.module?.slug === moduleSlug);
  };

  const canAccessReports = hasAccess('reports');
  const canAccessDafSpace = hasAccess('daf_space');

  return {
    currentPlan,
    isDafClient,
    activeModules,
    hasAccess,
    hasModule,
    canAccessReports,
    canAccessDafSpace,
    loading
  };
}
